import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, PenLine, Plus, Send } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { useToast } from '../components/ui/useToast';
import { ModaleSignatureDocument } from '../components/signature/ModaleSignatureDocument';
import { ModaleEntretienAccompagnement } from '../components/accompagnement/ModaleEntretienAccompagnement';
import { usePerimetre } from '../site/usePerimetre';
import { useDocumentsPedagogiques } from '../lib/usePedagogique';
import {
  LIBELLES_STATUT_ACTION,
  urlDocumentAccompagnement,
  useAccompagnements,
  useConvoquerExamen,
  useModifierAction,
  type LigneAccompagnement,
  type LigneAction,
  type LigneConvocationExamen,
  type StatutAction,
} from '../lib/useAccompagnement';
import { ErreurApi } from '../lib/api';

type Onglet = 'entretiens' | 'actions' | 'convocations';
type FiltreActions = 'OUVERTES' | 'RETARD' | 'TOUTES';

function jourLisible(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

const CLASSE_ACTION =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition [@media(pointer:coarse)]:min-h-[40px]';

/**
 * Écran « Accompagnement personnalisé » du pôle pédagogique (CDC §6.8).
 *
 * Trois vues : les entretiens — fiche sur le modèle client, signée sur la
 * tablette par l'apprenant et le conseiller, ce qui clôt le suivi ; le tableau
 * des actions engagées, suivies une à une jusqu'à leur réalisation ; les
 * convocations à l'examen, envoyées par session à l'apprenant avec copie à
 * l'employeur. Tout ce qui est compté vient de la base.
 */
export function PedAccompagnementPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useAccompagnements(site);
  // La liste des apprenants du périmètre est celle de l'écran des documents :
  // elle porte le nom et l'antenne.
  const { data: perimetre } = useDocumentsPedagogiques(site);

  const [onglet, setOnglet] = useState<Onglet>('entretiens');
  const [saisie, setSaisie] = useState(false);
  const [aSigner, setASigner] = useState<LigneAccompagnement | null>(null);

  const compteurs = data?.compteurs;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Accompagnement personnalisé"
        sousTitre="Pôle pédagogique · fiche d'entretien, suivi des actions, convocations à l'examen"
        actions={
          <BoutonPrincipal onClick={() => setSaisie(true)} disabled={!perimetre}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            Nouvel entretien
          </BoutonPrincipal>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Suivis en cours"
          valeur={compteurs?.suivisEnCours ?? '—'}
          detail={`${compteurs?.apprenantsSuivis ?? 0} apprenant(s) suivi(s)`}
          ton="navy"
        />
        <Compteur libelle="Suivis clôturés" valeur={compteurs?.suivisClotures ?? '—'} detail="fiche signée" ton="mint" />
        <Compteur
          libelle="Actions ouvertes"
          valeur={compteurs?.actionsOuvertes ?? '—'}
          detail={`${compteurs?.actionsEnRetard ?? 0} en retard`}
          ton="warm"
        />
        <Compteur
          libelle="Convocations à envoyer"
          valeur={compteurs?.convocationsAEnvoyer ?? '—'}
          detail="examens à venir"
          ton="blue"
        />
      </div>

      {error instanceof ErreurApi && <MessageErreur message={error.message} />}

      <nav className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-card" aria-label="Vues de l'accompagnement">
        {(
          [
            ['entretiens', 'Entretiens'],
            ['actions', 'Suivi des actions'],
            ['convocations', "Convocations à l'examen"],
          ] as const
        ).map(([cle, libelle]) => (
          <button
            key={cle}
            type="button"
            onClick={() => setOnglet(cle)}
            aria-current={onglet === cle ? 'page' : undefined}
            className={`min-h-[44px] min-w-0 flex-1 rounded-xl px-4 text-sm font-semibold transition sm:flex-none ${
              onglet === cle ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-50 hover:text-codialis-navy'
            }`}
          >
            {libelle}
          </button>
        ))}
      </nav>

      {isLoading && <Chargement />}

      {data && onglet === 'entretiens' && <ListeEntretiens suivis={data.suivis} onSigner={setASigner} />}
      {data && onglet === 'actions' && <TableauActions actions={data.actions} />}
      {data && onglet === 'convocations' && <ListeConvocations convocations={data.convocations} />}

      {saisie && perimetre && (
        <ModaleEntretienAccompagnement apprenants={perimetre.apprenants} onFermer={() => setSaisie(false)} />
      )}
      {aSigner?.fiche?.demandeSignatureId && (
        <ModaleSignatureDocument
          libelle={`Fiche d’entretien · ${aSigner.apprenant}`}
          urlLecture={urlDocumentAccompagnement(aSigner.fiche.documentId)}
          demandeSignatureId={aSigner.fiche.demandeSignatureId}
          signataires={aSigner.fiche.signataires}
          empreinteDocument={aSigner.fiche.empreinteDocument}
          onFermer={() => setASigner(null)}
        />
      )}
    </div>
  );
}

function ListeEntretiens({
  suivis,
  onSigner,
}: {
  suivis: LigneAccompagnement[];
  onSigner: (suivi: LigneAccompagnement) => void;
}): JSX.Element {
  return (
    <Carte titre="Entretiens d’accompagnement">
      {suivis.length === 0 && (
        <div className="px-4 py-8 text-sm text-codialis-gray sm:px-6">
          Aucun entretien d’accompagnement n’est enregistré sur ce périmètre.
        </div>
      )}
      {suivis.map((suivi) => {
        const realisees = suivi.actions.filter((action) => action.statut === 'REALISEE').length;
        const signes = suivi.fiche?.signataires.filter((signataire) => signataire.signeLe).length ?? 0;
        return (
          <div key={suivi.id} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
            <div className="min-w-0 flex-1">
              <Link to={`/learners/${suivi.apprenantId}`} className="text-sm font-semibold text-codialis-navy hover:text-codialis-sky">
                {suivi.apprenant}
              </Link>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-codialis-gray">
                <PastilleSite site={suivi.siteCode} />
                Entretien du {horodatage(suivi.dateEntretien)}
                {suivi.referent ? ` · ${suivi.referent}` : ''} · {realisees}/{suivi.actions.length} action(s) réalisée(s)
              </div>
            </div>
            <Pastille texte={suivi.statutLibelle} ton={suivi.statutTon.replace('badge-', '')} />
            {suivi.statut === 'A_SIGNER' && (
              <span className="text-[11px] text-codialis-gray">
                {signes}/{suivi.fiche?.signataires.length ?? 0} signé(s)
              </span>
            )}
            {suivi.statut === 'CLOTURE' && suivi.clotureLe && (
              <span className="text-[11px] text-codialis-gray">le {horodatage(suivi.clotureLe)}</span>
            )}
            {suivi.fiche && (
              <a
                href={urlDocumentAccompagnement(suivi.fiche.documentId)}
                target="_blank"
                rel="noreferrer"
                className={`${CLASSE_ACTION} text-codialis-sky hover:underline`}
              >
                <FileText className="h-3.5 w-3.5" aria-hidden />
                Fiche
              </a>
            )}
            {suivi.statut === 'A_SIGNER' && (
              <button
                type="button"
                onClick={() => onSigner(suivi)}
                className={`${CLASSE_ACTION} bg-codialis-mint text-codialis-navy hover:brightness-95`}
              >
                <PenLine className="h-3.5 w-3.5" aria-hidden />
                Faire signer
              </button>
            )}
          </div>
        );
      })}
    </Carte>
  );
}

function TableauActions({ actions }: { actions: LigneAction[] }): JSX.Element {
  const notifier = useToast();
  const modifier = useModifierAction();
  const [filtre, setFiltre] = useState<FiltreActions>('OUVERTES');

  const visibles = useMemo(
    () =>
      actions.filter((action) =>
        filtre === 'TOUTES'
          ? true
          : filtre === 'RETARD'
            ? action.enRetard
            : action.statut === 'A_FAIRE' || action.statut === 'EN_COURS',
      ),
    [actions, filtre],
  );

  return (
    <Carte
      titre="Suivi des actions d’accompagnement"
      actions={
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer les actions">
          {(
            [
              ['OUVERTES', 'Ouvertes'],
              ['RETARD', 'En retard'],
              ['TOUTES', 'Toutes'],
            ] as const
          ).map(([cle, libelle]) => (
            <button
              key={cle}
              type="button"
              aria-pressed={filtre === cle}
              onClick={() => setFiltre(cle)}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
                filtre === cle ? 'bg-codialis-navy text-white' : 'border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
              }`}
            >
              {libelle}
            </button>
          ))}
        </div>
      }
    >
      {modifier.error instanceof ErreurApi && (
        <div className="px-4 pt-3 sm:px-6">
          <MessageErreur message={modifier.error.message} />
        </div>
      )}
      {visibles.length === 0 ? (
        <div className="px-4 py-8 text-sm text-codialis-gray sm:px-6">Aucune action dans cette vue.</div>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Action</th>
                <th className="px-4 py-3 text-left font-bold">Apprenant</th>
                <th className="px-4 py-3 text-left font-bold">Échéance</th>
                <th className="px-4 py-3 text-left font-bold">Responsable</th>
                <th className="px-4 py-3 text-right font-bold">État</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibles.map((action) => (
                <tr key={action.id} className="transition hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-codialis-navy">{action.libelle}</div>
                    <div className="text-[11px] text-codialis-gray">
                      Entretien du {jourLisible(action.dateEntretien)}
                      {action.realiseeLe ? ` · réalisée le ${jourLisible(action.realiseeLe)}` : ''}
                      {action.commentaire ? ` · ${action.commentaire}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-codialis-navy">
                    {action.apprenant}
                    <span className="ml-1.5 inline-block">
                      <PastilleSite site={action.siteCode} />
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {action.echeance ? (
                      <span className={action.enRetard ? 'font-semibold text-red-700' : 'text-codialis-navy'}>
                        {jourLisible(action.echeance)}
                        {action.enRetard ? ' · en retard' : ''}
                      </span>
                    ) : (
                      <span className="text-codialis-gray">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-codialis-gray">{action.responsable ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <label>
                      <span className="sr-only">État de l’action « {action.libelle} »</span>
                      <select
                        value={action.statut}
                        onChange={(e) =>
                          modifier.mutate(
                            { id: action.id, statut: e.target.value as StatutAction },
                            { onSuccess: ({ message }) => notifier(message) },
                          )
                        }
                        disabled={modifier.isPending}
                        className="min-h-[40px] rounded-lg border border-gray-200 bg-white px-2 text-xs text-codialis-navy"
                      >
                        {Object.entries(LIBELLES_STATUT_ACTION).map(([valeur, libelle]) => (
                          <option key={valeur} value={valeur}>
                            {libelle}
                          </option>
                        ))}
                      </select>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Carte>
  );
}

function LigneConvocation({ convocation }: { convocation: LigneConvocationExamen }): JSX.Element {
  const notifier = useToast();
  const convoquer = useConvoquerExamen();
  const [ouverte, setOuverte] = useState(false);
  const [heure, setHeure] = useState('');
  const [lieu, setLieu] = useState('');

  return (
    <div className="space-y-3 px-4 py-3 sm:px-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-codialis-navy">
            {convocation.formation ?? 'Sans formation rattachée'}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-codialis-gray">
            <PastilleSite site={convocation.siteCode} />
            Examen le {jourLisible(convocation.dateExamen)} · {convocation.convoques}/{convocation.effectif} convoqué(s)
          </div>
        </div>
        <Pastille texte={convocation.statutLibelle} ton={convocation.statutTon.replace('badge-', '')} />
        {!ouverte && (
          <button
            type="button"
            onClick={() => setOuverte(true)}
            className={`${CLASSE_ACTION} ${
              convocation.statut === 'ENVOYEES'
                ? 'border border-gray-200 text-codialis-navy hover:bg-gray-50'
                : 'bg-codialis-navy text-white hover:bg-codialis-deep'
            }`}
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
            {convocation.statut === 'ENVOYEES' ? 'Renvoyer' : 'Générer et envoyer'}
          </button>
        )}
      </div>
      {ouverte && (
        <div className="space-y-3 rounded-xl border border-dashed border-codialis-sky/40 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[9rem_1fr]">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Heure</span>
              <input
                type="time"
                value={heure}
                onChange={(e) => setHeure(e.target.value)}
                className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy"
              />
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Lieu</span>
              <input
                value={lieu}
                onChange={(e) => setLieu(e.target.value)}
                maxLength={300}
                placeholder="Établissement, adresse, salle"
                className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy"
              />
            </label>
          </div>
          <p className="text-[11px] text-codialis-gray">
            Chaque apprenant de la session reçoit sa convocation par e-mail et dans son espace ; l’employeur du
            contrat en cours est mis en copie.
          </p>
          {convoquer.error instanceof ErreurApi && <MessageErreur message={convoquer.error.message} />}
          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal
              onClick={() =>
                convoquer.mutate(
                  {
                    formationCode: convocation.formationCode,
                    siteCode: convocation.siteCode,
                    dateExamen: convocation.dateExamen,
                    heure,
                    lieu,
                  },
                  {
                    onSuccess: ({ message }) => {
                      notifier(message);
                      setOuverte(false);
                    },
                  },
                )
              }
              disabled={convoquer.isPending || !heure || lieu.trim().length < 2}
            >
              {convoquer.isPending ? 'Envoi…' : `Envoyer ${convocation.effectif} convocation(s)`}
            </BoutonPrincipal>
            <BoutonSecondaire onClick={() => setOuverte(false)} disabled={convoquer.isPending}>
              Annuler
            </BoutonSecondaire>
          </div>
        </div>
      )}
    </div>
  );
}

function ListeConvocations({ convocations }: { convocations: LigneConvocationExamen[] }): JSX.Element {
  return (
    <Carte titre="Convocations à l'examen, par session">
      {convocations.length === 0 && (
        <div className="px-4 py-8 text-sm text-codialis-gray sm:px-6">
          Aucune date d’examen n’est renseignée sur ce périmètre.
        </div>
      )}
      {convocations.map((convocation) => (
        <LigneConvocation key={convocation.cle} convocation={convocation} />
      ))}
    </Carte>
  );
}
